import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';
import ClearIcon from '@mui/icons-material/Clear';
import CircularProgress from '@mui/material/CircularProgress';
import FormHelperText from '@mui/material/FormHelperText';
import React, { useCallback, useContext, useRef, useState } from 'react';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import API from '../../api';
import { Button } from '../../components';
import { SnackbarContext } from '../../contexts';
import { FormInputLabel } from './FormInputLabel';

const DEFAULT_MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
const ACCEPT_FILE_TYPES = {
  image: 'image/gif, image/jpeg, image/png',
  sound: 'audio/mp3',
  video: 'video/mp4',
  file: '*',
};

const getAcceptFileList = (type) => ACCEPT_FILE_TYPES[type] || ACCEPT_FILE_TYPES.file;

/**
 * 파일 크기를 바이트 단위로 변환
 * @param {number} bytes - 바이트 크기
 * @returns {string} - 읽기 쉬운 크기 문자열
 */
const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
};

const renderPreview = (url, fileType) => {
  if (!url) {
    return <div className='empty__box' />;
  }

  const handleOpen = () => window.open(url, '_blank');
  const fileName = url.split('/').pop() || '';

  if (fileType === 'image') {
    return <img onClick={handleOpen} src={url} alt='Preview' className='w-full h-full object-cover' />;
  }

  return (
    <div className='file__box'>
      <span onClick={handleOpen}>{fileName}</span>
    </div>
  );
};

const MultipleFileUploaderInner = React.forwardRef((props, ref) => {
  const {
    value = [],
    onChange,
    label,
    name,
    path,
    fileType = 'file',
    max = 10,
    required = false,
    readOnly = false,
    subLabel,
    error,
    maxFileSize = DEFAULT_MAX_FILE_SIZE,
    className,
    style,
  } = props;

  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({ current: 0, total: 0 });
  const { updateSnackbar } = useContext(SnackbarContext);
  const inputId = useRef(`file-uploader-input-${name || Math.random().toString(36).substr(2, 9)}`);

  /**
   * 파일 크기 검증
   * @param {File[]} files - 검증할 파일 배열
   * @returns {{ valid: boolean, error?: string }}
   */
  const validateFileSize = useCallback(
    (files) => {
      if (!files || files.length === 0) {
        return { valid: false, error: '파일을 선택해주세요.' };
      }

      const totalSize = Array.from(files).reduce((sum, file) => sum + file.size, 0);

      if (totalSize > maxFileSize) {
        return {
          valid: false,
          error: `파일 크기가 너무 큽니다. 최대 ${formatFileSize(
            maxFileSize,
          )}까지 업로드 가능합니다. (현재: ${formatFileSize(totalSize)})`,
        };
      }

      return { valid: true };
    },
    [maxFileSize],
  );

  const removeFileInput = useCallback(() => {
    const input = document.getElementById(inputId.current);
    if (input) {
      input.value = '';
    }
  }, []);

  /**
   * 단일 파일 업로드
   * @param {File} file - 업로드할 파일
   * @returns {Promise<string>} - 업로드된 파일의 URL
   */
  const uploadSingleFile = useCallback(
    async (file) => {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('path', path);

      const res = await API.Common.PostFile(formData);
      return res.data.cloudUrl;
    },
    [path],
  );

  /**
   * 여러 파일을 순차적으로 업로드
   * @param {File[]} files - 업로드할 파일 배열
   */
  const uploadFilesSequentially = useCallback(
    async (files) => {
      setLoading(true);
      setUploadProgress({ current: 0, total: files.length });

      const uploadedUrls = [];
      const currentValue = value || [];

      try {
        for (let i = 0; i < files.length; i++) {
          setUploadProgress({ current: i + 1, total: files.length });
          const url = await uploadSingleFile(files[i]);
          uploadedUrls.push(url);
        }

        // 모든 파일 업로드 완료 후 배열로 저장
        const newValue = [...currentValue, ...uploadedUrls];
        onChange(newValue);
        removeFileInput();
      } catch (err) {
        updateSnackbar({
          type: 'error',
          open: true,
          message: err.data?.message || '파일 업로드 중 오류가 발생했습니다.',
        });
      } finally {
        setLoading(false);
        setUploadProgress({ current: 0, total: 0 });
      }
    },
    [value, onChange, uploadSingleFile, updateSnackbar, removeFileInput],
  );

  /**
   * 파일 선택 핸들러
   * @param {Event} e - 파일 입력 이벤트
   */
  const handleFileChange = useCallback(
    (e) => {
      const files = e.target.files;
      if (!files || files.length === 0) return;

      const fileArray = Array.from(files);

      // 파일 크기 검증
      const validation = validateFileSize(fileArray);
      if (!validation.valid) {
        updateSnackbar({
          type: 'error',
          open: true,
          message: validation.error,
        });
        removeFileInput();
        return;
      }

      // 최대 개수 검증
      const currentCount = value?.length || 0;
      const remainingSlots = max - currentCount;
      if (fileArray.length > remainingSlots) {
        updateSnackbar({
          type: 'error',
          open: true,
          message: `최대 ${max}개까지 업로드 가능합니다. (현재: ${currentCount}개, 추가 가능: ${remainingSlots}개)`,
        });
        removeFileInput();
        return;
      }

      // 파일 업로드 시작
      uploadFilesSequentially(fileArray);
    },
    [value, max, validateFileSize, uploadFilesSequentially, updateSnackbar, removeFileInput],
  );

  const handleRemove = useCallback(
    (index) => {
      const newValue = value.filter((_, i) => i !== index);
      onChange(newValue);
      removeFileInput();
    },
    [value, onChange, removeFileInput],
  );

  const handleAddButtonClick = useCallback(() => {
    if (!readOnly) {
      document.getElementById(inputId.current)?.click();
    }
  }, [readOnly]);

  const renderAddButton = () => {
    const canAddMore = (value?.length || 0) < max && !readOnly;
    if (!canAddMore) return null;

    const progressLabel =
      loading && uploadProgress.total > 0
        ? `${t('FILE_CHOOSE')} (${uploadProgress.current}/${uploadProgress.total})`
        : t('FILE_CHOOSE');

    if (fileType === 'file') {
      return (
        <li className='add' onClick={handleAddButtonClick}>
          <Button
            type='info-square'
            loading={loading}
            style={{ width: '100%', height: '50px' }}
            label={progressLabel}
          />
        </li>
      );
    }

    return (
      <li className={loading ? 'add-image loading' : 'add-image'} onClick={handleAddButtonClick}>
        {loading ? (
          <div className='flex flex-col items-center gap-2'>
            <CircularProgress size={30} />
            {uploadProgress.total > 0 && (
              <span className='text-xs'>
                {uploadProgress.current}/{uploadProgress.total}
              </span>
            )}
          </div>
        ) : (
          <div className='cursor-pointer h-full w-full min-h-[150px] flex items-center gap-2 justify-center border border-solid border-gray-300 rounded-md'>
            <AddPhotoAlternateOutlinedIcon />
            <span className='text-sm font-medium'>{t('FILE_CHOOSE')}</span>
          </div>
        )}
      </li>
    );
  };

  const fileCount = value?.length || 0;

  return (
    <article className={`form-file-multiple-uploader ${className || ''}`} style={style || {}}>
      <header>
        <div className='flex items-center gap-1'>
          {label && <FormInputLabel label={label} isRequired={required} />}
          <em>
            ({fileCount}/{max})
          </em>
        </div>
        {subLabel && (
          <div className='sub-label__box'>
            <span className='sub-label'>{t(subLabel)}</span>
          </div>
        )}
      </header>
      <ul
        className={`grid grid-cols-4 gap-4 border border-solid rounded-md p-4 ${fileType} ${
          error ? 'border-red-500' : 'border-gray-300'
        }`}
      >
        {fileCount < max && renderAddButton()}
        {value?.map((item, index) => (
          <li
            key={`${item}-${index}`}
            className='border h-[150px] border-solid border-gray-300 relative rounded-md overflow-hidden'
          >
            {renderPreview(item, fileType)}
            {!readOnly && (
              <div className='absolute top-2 right-2'>
                <ClearIcon onClick={() => handleRemove(index)} />
              </div>
            )}
          </li>
        ))}
      </ul>
      <div className='input__box'>
        <input
          ref={ref}
          type='file'
          accept={getAcceptFileList(fileType)}
          id={inputId.current}
          multiple
          onChange={handleFileChange}
          disabled={loading || readOnly}
        />
      </div>
      {error && <FormHelperText error>{error.message}</FormHelperText>}
    </article>
  );
});

MultipleFileUploaderInner.displayName = 'MultipleFileUploaderInner';

/**
 * react-hook-form과 함께 사용할 수 있는 MultipleFileUploader 컴포넌트
 * @param {Object} props
 * @param {Object} props.control - react-hook-form의 control 객체 (Controller 사용 시)
 * @param {string} props.name - 필드 이름 (Controller 사용 시 필수)
 * @param {string[]} props.value - 파일 URL 배열 (직접 사용 시)
 * @param {Function} props.onChange - 변경 핸들러 (직접 사용 시)
 * @param {string} props.label - 라벨
 * @param {string} props.path - 파일 업로드 경로
 * @param {'file'|'image'|'sound'|'video'} props.fileType - 파일 타입
 * @param {number} props.max - 최대 파일 개수
 * @param {boolean} props.required - 필수 여부
 * @param {boolean} props.readOnly - 읽기 전용 여부
 * @param {string} props.subLabel - 서브 라벨
 * @param {Object} props.error - 에러 객체
 * @param {number} props.maxFileSize - 최대 파일 크기 (bytes, 기본값: 10MB)
 */
export const MultipleFileUploader = (props) => {
  const { control, error, name, ...rest } = props;

  // Controller를 사용하는 경우
  if (control && name) {
    return (
      <Controller
        control={control}
        name={name}
        rules={rest.rules}
        render={({ field, fieldState }) => {
          return (
            <MultipleFileUploaderInner {...rest} value={field.value || []} onChange={field.onChange} error={error} />
          );
        }}
      />
    );
  }

  // 직접 사용하는 경우
  return <MultipleFileUploaderInner {...rest} />;
};

export default MultipleFileUploader;
